// Central content config for the PARKORA landing page.
// Edit copy here without touching component markup.

export const brand = {
  name: "PARKORA",
  tagline: "Find. Book. Park Safe.",
  parentCompany: "A product of Maya Media Works",
  launch: "Launching in Coimbatore in 2026",
  launchCity: "Coimbatore, Tamil Nadu, India",
};

export const nav = {
  links: [
    { label: "How It Works", href: "#how-it-works" },
    { label: "For Owners", href: "#for-owners" },
    { label: "Why PARKORA", href: "#why-parkora" },
    { label: "FAQ", href: "#faq" },
  ],
  badge: "Coimbatore · 2026",
  cta: "Get Early Access",
};

export const hero = {
  badge: "PARKING MARKETPLACE · COIMBATORE",
  location: "Starting in Coimbatore",
  headlineTop: "FIND PARKING.",
  headlineBottom: "WITHOUT THE HASSLE.",
  subheadline:
    "PARKORA helps you discover and book nearby parking spaces before you arrive.",
  primaryCta: "Get Early Access",
  secondaryCta: "List Your Parking Space",
  tagline: "Find. Book. Park Safe.",
  statusCard: {
    title: "Parking near you",
    spaces: "12 spaces available",
    distance: "250 m away",
    price: "₹30/hr",
    tag: "Verified",
  },
  demo: {
    prompt: "Where are you going?",
    query: "Singanallur",
    results: [
      { distance: "0.2 km", price: "₹30/hr", status: "Available" },
      { distance: "0.4 km", price: "₹25/hr", status: "Available" },
      { distance: "0.6 km", price: "₹20/hr", status: "Available" },
    ],
    disclaimer: "Product preview — for illustration only",
  },
};

export const problem = {
  headline: "Parking shouldn’t be a treasure hunt.",
  steps: ["5 minutes", "10 minutes", "15 minutes", "No parking."],
  line1: "You’re already late.",
  line2: "Why are you still looking for parking?",
  resolution: "PARKORA changes that.",
};

export const solution = {
  headline: "Your parking spot is just a few taps away.",
  body: "PARKORA connects drivers looking for parking with available private parking spaces near their destination.",
  cards: [
    {
      number: "01",
      title: "FIND",
      text: "Search where you’re going and discover nearby parking.",
    },
    {
      number: "02",
      title: "BOOK",
      text: "Choose the parking that works for you and reserve it.",
    },
    {
      number: "03",
      title: "PARK SAFE",
      text: "Arrive, use your check-in code or QR to check in, park and check out securely.",
    },
  ],
};

export const howItWorks = {
  headline: "Six simple steps.",
  headlineAccent: "Zero parking stress.",
  steps: [
    { number: "01", title: "SEARCH", text: "Enter your destination." },
    {
      number: "02",
      title: "CHOOSE",
      text: "Compare nearby spaces by distance, price and value.",
    },
    { number: "03", title: "BOOK", text: "Reserve your parking space." },
    {
      number: "04",
      title: "ARRIVE",
      text: "Reach the location and use your check-in code or QR to check in.",
    },
    { number: "05", title: "PARK", text: "Park safely." },
    {
      number: "06",
      title: "CHECKOUT",
      text: "Complete checkout and pay for your actual parking time.",
    },
  ],
};

export const productPreview = {
  eyebrow: "Product preview",
  title: "Availability is the product.",
  titleAccent: "So it comes first.",
  body: "Bike and car parking are genuinely different spaces at different prices, and Coimbatore runs on two wheels — so the vehicle you're parking sets the price and the list before you even search.",
  disclaimer: "This is only a visual prototype — for illustration only.",
  location: "Gandhipuram",
  credits: "240",
  vehicles: {
    bike: { label: "Bike", spots: "62 spots open", liveCount: "62 bike spots" },
    car: { label: "Car", spots: "18 spots open", liveCount: "18 car spots" },
  },
  screens: [
    {
      key: "home",
      tag: "Home",
      hot: true,
      note: "The vehicle selector is the signature element. It sets the price, filters the list, and gives the colourful bike/car moment — above the fold, without pushing search down.",
    },
    {
      key: "arrival",
      tag: "Arrival",
      note: "The OTP is generated on the phone, not sent by SMS. Free, instant, and it still works in a basement with no signal.",
    },
    {
      key: "live",
      tag: "Live session",
      note: "Checkout needs the owner's approval. Naming that honestly, and showing the request the moment it's sent, is what stops it becoming a support ticket.",
    },
  ],
  spots: [
    {
      tier: "best",
      badge: "Closest + cheapest",
      distance: "200 m",
      tag: "COVERED",
      name: "Lakshmi Complex",
      meta: "Gandhipuram · 3 min walk",
      freeNow: "4 free now",
      chips: ["CCTV", "Open till 10pm"],
      price: "30",
      unit: "2 hr min",
      after: "₹15/hr after",
    },
    {
      tag: "GATED",
      name: "Sundaram Textiles",
      meta: "Cross Cut Road · 6 min walk",
      freeNow: "9 free now",
      chips: ["Attendant"],
      price: "24",
      unit: "2 hr min",
      after: "₹12/hr after",
    },
    {
      tag: "OPEN",
      name: "Ganesh Residency",
      meta: "100 Feet Road · 9 min walk",
      freeNow: "2 free now",
      chips: ["Home owner"],
      price: "20",
      unit: "2 hr min",
      after: "₹10/hr after",
    },
  ],
  maya: {
    home: "Find me covered bike parking near Brookefields",
    arrival: "Owner not responding — what do I do?",
    live: "Can I extend by an hour?",
  },
  arrival: {
    bookingId: "Booking #PK-4821",
    location: "Lakshmi Complex",
    otp: ["4", "7", "2", "9"],
    owner: "Ramesh K.",
    slot: "B-04, covered",
    minimum: "2 hours · ₹30",
    after: "₹15 / hour",
  },
  live: {
    location: "Lakshmi Complex · B-04",
    timer: "1:45",
    timerLabel: "Into your 2-hour minimum",
    progress: 42,
    started: "9:41 AM",
    minimumCharge: "₹30",
    extraTime: "₹0",
    credits: "− ₹6",
    due: "₹24",
  },
};

export const whyParkora = {
  headline: "Parking made smarter.",
  features: [
    {
      title: "VERIFIED SPACES",
      text: "Discover parking spaces and verified parking partners.",
    },
    {
      title: "SAVE TIME",
      text: "Spend less time driving around looking for a place to park.",
    },
    {
      title: "TRANSPARENT PRICING",
      text: "See parking rates before you book.",
    },
    {
      title: "CODE OR QR CHECK-IN",
      text: "Secure arrival and checkout through a simple verification process.",
    },
  ],
};

export const trust = {
  headline: "Because your parking should feel predictable.",
  points: [
    "Verified owner identity",
    "Clear pricing",
    "Check-in code or QR",
    "Checkout confirmation",
    "Booking chat with the owner",
  ],
};

export const ownerSection = {
  headline: "Have unused parking space?",
  subheadline: "Turn your unused space into an opportunity.",
  body: "List home parking, shop spaces, office parking, apartment spaces, restaurant spaces, or any other suitable vacant space.",
  spaceTypes: [
    "Home parking",
    "Shop spaces",
    "Office parking",
    "Apartment spaces",
    "Restaurant spaces",
    "Extra / private parking areas",
  ],
  cta: "List Your Space",
  secondaryCta: "Become a PARKORA Partner",
  highlight: "You set the price.",
  highlightSub: "PARKORA’s commission is explained in your partner agreement.",
  earnLine: "Earn when your space gets booked.",
};

export const coimbatore = {
  headline: "Coimbatore is our first stop.",
  body: "Built for the way Coimbatore moves.",
  launchLine: "Launching in Coimbatore in 2026.",
  areas: [
    "Singanallur",
    "Gandhipuram",
    "RS Puram",
    "Peelamedu",
    "Ukkadam",
    "Saibaba Colony",
  ],
  areaTag: "Launch Area",
  cta: "Get Launch Updates",
};

export const waitlist = {
  headline: "Be among the first to use PARKORA.",
  vehicleTypes: ["Car", "Bike", "Both"],
  cta: "GET EARLY ACCESS",
  successTitle: "You’re on the list!",
  successSub: "We’ll let you know when bookings open in your area.",
};

export const ownerWaitlist = {
  headline: "Have a parking space?",
  cta: "Register Your Space",
  submitCta: "REGISTER MY SPACE",
  spaceTypes: ["Home", "Shop", "Office", "Apartment", "Restaurant", "Other"],
  successTitle: "You’re registered!",
  successSub: "We’ll reach out before launch.",
};

export const faq = {
  headline: "Frequently asked questions",
  items: [
    {
      q: "What is PARKORA?",
      a: "PARKORA is a parking marketplace that helps people discover and book available parking spaces near their destination.",
    },
    {
      q: "When is PARKORA launching?",
      a: "PARKORA is launching in Coimbatore in 2026. We’re currently registering parking space owners — join the early-access list to hear when bookings open in your area.",
    },
    {
      q: "Is PARKORA free to use?",
      a: "Creating an account is free. When you book, you pay a small booking fee shown in the app before you pay. At checkout you pay the parking charge for the time you actually parked, at the hourly rate shown on the listing. Payments are processed securely by Razorpay.",
    },
    {
      q: "Can I list my unused parking space?",
      a: "Yes. Eligible homeowners, businesses and other authorized space providers can apply to list parking.",
    },
    {
      q: "How does parking verification work?",
      a: "Parking spaces and owners will go through PARKORA’s verification process before becoming available to customers.",
    },
    {
      q: "How does check-in work?",
      a: "After booking, customers get a check-in code (and QR code) in the app. The owner scans the QR code or enters the code at the parking location to start the parking session.",
    },
    {
      q: "Can I earn from my unused parking space?",
      a: "Yes. Owners can set their availability and pricing and earn when customers book their space.",
    },
    {
      q: "Where is PARKORA launching first?",
      a: "Coimbatore, Tamil Nadu.",
    },
  ],
};

export const finalCta = {
  headlineTop: "Your next parking spot",
  headlineBottom: "is closer than you think.",
  subheadline: "PARKORA is launching in Coimbatore in 2026.",
  cta1: "GET EARLY ACCESS",
  cta2: "LIST YOUR SPACE",
};

export const footer = {
  tagline: "Find. Book. Park Safe.",
  parentCompany: "A product of Maya Media Works",
  links: [
    { label: "Home", href: "#top" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "For Owners", href: "#for-owners" },
    { label: "FAQ", href: "#faq" },
    { label: "Contact", href: "#contact" },
    { label: "Privacy Policy", href: "/privacy-policy" },
    { label: "Terms & Conditions", href: "/terms-and-conditions" },
    { label: "Delete Account", href: "/delete-account" },
  ],
  // Same details as the published Terms §17 / Privacy §16 — keep in sync.
  contact: {
    name: "Maya Media Works (PARKORA)",
    address:
      "No. 8, Venkateshwara Nagar, Old Elgi School Backside, Vellalore, Coimbatore – 641111, Tamil Nadu, India",
    phone: "+91 97903 78203",
    phoneHref: "tel:+919790378203",
    email: "parkoracbe@gmail.com",
  },
  socials: ["Instagram", "Facebook", "YouTube", "LinkedIn"],
  copyright: "© 2026 Maya Media Works. PARKORA. All rights reserved.",
};
